<template>
  <select class="form-select" :id="id" :disabled="disabled" v-if="asSelect"
          :value="selected ? selected.code : ''" @change="pick($event.target.value)">
    <option value="">[Selecione o motivo]</option>
    <optgroup v-for="group in grouped" :label="group.label" :key="group.key">
      <option v-for="item in group.items" :value="item.code" :key="item.code">
        {{ item.code }} · {{ item.description }}
      </option>
    </optgroup>
  </select>
  <dx-autocomplete
    v-else
    :id="id"
    v-model="selected"
    :source="find"
    label="code"
    track-by="code"
    :custom-label="customLabel"
    :debounce="300"
    :min-len="0"
    :placeholder="placeholder"
    :disabled="disabled"
    @select="select"
    @blur="$emit('blur')"
  >
    <template v-slot="{ item }">
      <div class="bg-code-option">
        <span class="bg-gcode"><span class="bg-gcode-code">{{ item.code }}</span></span>
        <span class="bg-code-option-desc">{{ item.description }}</span>
        <span class="bg-code-option-group text-xs text-gray">{{ groupName(item) }}</span>
      </div>
    </template>
    <button slot="action" class="btn btn-neutral btn-action input-group-btn btn-icon"
            tabindex="-1" @click.prevent="clear" :disabled="disabled || !selected">
      <fa-icon :icon="['fal', 'times']"/>
    </button>
  </dx-autocomplete>
</template>

<script>
import localforage from 'localforage';

const GROUPS = {
  beneficiario: 'beneficiário',
  guia: 'guia',
  autorizacao: 'autorização',
  material: 'material',
  recurso: 'recurso',
  odonto: 'odontologia',
  tecnica: 'técnica',
  particular: 'uso particular',
};

const RECENT_KEY = 'billingGlossRecentCodes';
const SELECT_LIMIT = 120;

export default {
  props: {
    value: {
      type: Object,
      default: null,
    },
    insuranceId: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: 'gloss-code',
    },
    placeholder: {
      type: String,
      default: 'Código ou descrição da Tabela 38',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: this.value,
      recent: [],
      all: [],
      total: 0,
    };
  },
  computed: {
    asSelect() {
      return this.total > 0 && this.all.length >= this.total;
    },
    grouped() {
      const groups = new Map();
      this.all.forEach((item) => {
        const key = item.group || 'outros';
        const current = groups.get(key) || { key, label: GROUPS[key] || key, items: [] };
        current.items.push(item);
        groups.set(key, current);
      });
      return [...groups.values()];
    },
  },
  watch: {
    value(value) {
      this.selected = value;
    },
  },
  mounted() {
    this.loadRecent();
    this.loadAll();
  },
  methods: {
    loadAll() {
      return this.$http.get('/tiss-gloss-codes', { params: { limit: SELECT_LIMIT } })
        .then(({ data }) => {
          this.all = data.items || [];
          this.total = data.total || 0;
        })
        .catch(() => {
          this.all = [];
          this.total = 0;
        });
    },
    pick(code) {
      this.select(this.all.find(item => item.code === code) || null);
    },
    customLabel(item) {
      return item && item.code ? `${item.code} · ${item.description}` : '';
    },
    groupName(item) {
      const label = GROUPS[item.group] || item.group || '';
      return item.recent ? `recente · ${label}` : label;
    },
    recentKey() {
      return this.insuranceId ? `${RECENT_KEY}:${this.insuranceId}` : RECENT_KEY;
    },
    loadRecent() {
      return localforage.getItem(this.recentKey())
        .then((items) => {
          this.recent = Array.isArray(items) ? items : [];
        })
        .catch(() => {
          this.recent = [];
        });
    },
    remember(item) {
      const entry = { code: item.code, description: item.description, group: item.group };
      const list = [entry, ...this.recent.filter(({ code }) => code !== item.code)].slice(0, 5);
      this.recent = list;
      localforage.setItem(this.recentKey(), list).catch(() => {});
    },
    find(search) {
      const term = String(search || '').trim();
      if (!term) {
        const recent = this.recent.map(item => ({ ...item, recent: true }));
        const codes = new Set(recent.map(({ code }) => code));
        return this.$http.get('/tiss-gloss-codes')
          .then(({ data }) => [...recent, ...data.items.filter(({ code }) => !codes.has(code))])
          .catch(() => recent);
      }
      return this.$http.get('/tiss-gloss-codes', { params: { search: term } })
        .then(({ data }) => {
          const recent = this.recent
            .filter(item => item.code.startsWith(term) || new RegExp(term, 'i').test(item.description))
            .map(item => ({ ...item, recent: true }));
          const codes = new Set(recent.map(({ code }) => code));
          return [...recent, ...data.items.filter(({ code }) => !codes.has(code))];
        })
        .catch(() => []);
    },
    select(item) {
      this.selected = item;
      if (item) this.remember(item);
      this.$emit('input', item);
      this.$emit('select', item);
    },
    clear() {
      this.selected = null;
      this.$emit('input', null);
      this.$emit('select', null);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.bg-code-option {
  align-items: center;
  display: flex;
  gap: $layout-spacing-sm;
  .bg-code-option-desc { flex: 1 1 auto; font-size: $font-size-sm; }
  .bg-code-option-group { white-space: nowrap; }
}
</style>
